<template>
  <div class="c-location-box__wrapper">
    <loading-spinner v-if="isLoading"></loading-spinner>
    <div class="c-location-box">
      <div class="c-location-box__content">
        <div v-show="!selectAddress">
          <div class="c-form-input">
            <label class="c-form-input__label" for="customerAccountReference">{{ customerAccountReferenceLabel }}</label>
            <input v-on:keyup.enter.prevent="getLocation" aria-describedby="customerAccountReferenceError" class="c-form-input__control" type="number" name="customerAccountReference" id="customerAccountReference" v-on:keypress="isNumber($event)" v-model.number="customerAccountReference" />
            <span class="c-form-input__validation-message" id="customerAccountReferenceError" v-if="searchError">{{ customerLocationValidationError }}</span>
          </div>
          <div class="c-form-input c-form-input--short">
            <label class="c-form-input__label" for="postcode" v-html="postcodeLabel"></label>
            <input v-on:keyup.enter.prevent="getLocation" aria-describedby="postcodeError" class="c-form-input__control" type="text" name="postcode" id="postcode" v-model="postcode" />
            <span class="c-form-input__validation-message" id="postcodeError" v-if="searchError">{{ customerLocationValidationError }}</span>
          </div>
          <div class="c-rap-rte" role="alert" v-html="locationNotFound" v-if="notFound"></div>
          <div class="c-rap-rte" role="alert" v-if="contactDetailsRequestError">
            <p>{{ contactDetailsRequestErrorText }}</p>
          </div> 
        </div>
        <div v-show="selectAddress">
          <div class="c-rap-address">
            <ul class="c-rap-address__list">
              <li class="c-rap-address__item" v-for="(suggestion, index) in suggestedAddresses" :key="suggestion.value">
                <input 
                  class="visually-hidden"
                  name="address" 
                  type="radio"
                  v-bind:id="addressId(index)" 
                  v-bind:value="suggestion.value"
                  v-model="address"
                  v-on:change="addressChange" />
                <label class="c-rap-address__label" v-bind:for="addressId(index)">{{ suggestion.label }}</label>
              </li>
            </ul>
            <span class="c-rap-address__validation-message" id="postcodeError" v-if="addressError">Address field is required</span>
          </div>
          <div class="c-rap-rte" role="alert" v-html="locationNotListed"></div>  
        </div>
      </div>
      <div class="c-location-box__extra" v-if="!locationValid">
        <div class="c-rap-rte" v-html="locationNotValid" ref="rte">
        </div>
      </div>
      <div class="c-location-box__actions">
        <template v-if="!selectAddress">
          <button 
            type="button"
            class="c-button c-button--brand c-button--text-center" 
            v-bind:disabled="isLoading" 
            v-on:click="backToWizard">
            {{ backButtonText }}
          </button>
          <button 
            type="button" 
            class="c-button c-button--brand c-button--text-center"
            v-bind:disabled="isLoading"
            v-on:click="getLocation">
            {{ relevantSearchButtonText }}
          </button>
        </template>
        <template v-if="selectAddress">
          <button 
            type="button"
            class="c-button c-button--brand c-button--text-center" 
            v-bind:disabled="isLoading" 
            v-on:click="resetLocation">
            {{ backButtonText }}
          </button>
          <button 
            type="button" 
            class="c-button c-button--brand c-button--text-center"
            v-bind:disabled="isLoading" 
            v-on:click="selectLocation">
            {{relevantContinueButtonText}}
          </button>
        </template>
      </div>
    </div>
  </div>
  </template>
  
  <script>
  import LoadingSpinner from "Components/consumer/_vue-shared/LoadingSpinner.vue";
  import rteMixin from './mixins/rteMixin.js';
  import jump from 'jump.js';
  
  export default {
    mixins: [rteMixin],
    components: {
      LoadingSpinner
    },
    props: {
      componentId: {
        required: true,
        type: String,
      },
      backButtonText: {
        required: true,
        type: String
      },
      continueButtonText: {
        required: true,
        type: String
      },
      contactDetailsRequestError: {
        required: true,
        type: Boolean,
      },
      contactDetailsRequestErrorText: {
        required: true,
        type: String,
      },
      customerAccountReferenceLabel: {
        required: true,
        type: String,
      },
      customerLocationValidationError: {
        required: true,
        type: String,
      },
      isLoading: {
        required: true,
        type: Boolean,
      },
      locationValid: {
        required: true,
        type: Boolean,
      },
      locationNotFound: {
        required: true,
        type: String,
      },
      locationNotListed: {
        required: true,
        type: String,
      },
      locationNotValid: {
        required: true,
        type: String,
      },
      notFound: {
        required: true,
        type: Boolean,
      },
      postcodeLabel: {
        required: true,
        type: String,
      },
      suggestedAddresses: {
        required: true,
        type: Array,
      },
      tryAgainButtonText: {
        required: true,
        type: String
      }
    },
    data() {
      return {
        address: '',
        addressSubmitted: false,
        customerAccountReference: '',
        postcode: '',
        searchSubmitted: false,
        postcodeResults: this.notFound,
      }
    },
    watch: {
      suggestedAddresses(newVal) {
        if (newVal.length === 1) {
          this.address = this.suggestedAddresses[0].value;
          this.$emit('addressChange', this.suggestedAddresses[0].value);
        }
      }
    },
    computed: {
      addressError() {
        return this.addressSubmitted === true && this.address === '';
      },
      searchError() {
        return this.searchSubmitted === true && this.customerAccountReference === '' && this.postcode === '';
      },
      selectAddress() {
        return this.suggestedAddresses.length;
      },
      relevantSearchButtonText() {
        return this.postcodeResults === true ? this.tryAgainButtonText : this.continueButtonText;
      },
      relevantContinueButtonText() {
        return this.locationValid === true ? this.continueButtonText : this.tryAgainButtonText;
      }
    },
    updated() {
      if(this.$refs.rte){
        this.makeTablesResponsive(this.$refs.rte);
      }
    },
    methods: {
      addressId(index) {
        return `address${index}`;
      },
      addressChange(event) {
        this.$emit('addressChange', event.target.value);
      },
      backToWizard() {
        this.$emit('stepChange', 1, true);
      },
      getLocation() {
        this.searchSubmitted = true;
        jump('.c-breadcrumb');
        if (this.customerAccountReference || this.postcode) {
          this.$emit('locationRequest', this.componentId, this.customerAccountReference, this.postcode);
          this.searchSubmitted = false;
        } else {
          this.postcodeResults = this.locationValue === false ? true : false;
        }
      },
      isNumber(e) {
        const charCode = (e.which) ? e.which : e.keyCode;
        if (charCode < 48 || charCode > 57) {
          e.preventDefault();
        }
      },
      resetLocation() {
        this.address = '';
        this.addressSubmitted = false;
        this.$emit('locationReset');
      },
      selectLocation() {
        this.addressSubmitted = true;
  
        if (this.address) {
          this.$emit('validateCustomer');
        }
      },
    }
  }
  </script>


// WEBPACK FOOTER //
// src/components/components/consumer/report-a-problem/_vue/LocationBox.vue